/**
 * BlockRenderer — renders an ordered array of page_blocks on the public website.
 *
 * Supported block types:
 *   hero         – full-width hero with title, subtitle, bg color/image, CTA button
 *   banner       – single-line announcement bar with optional button
 *   stats        – row of stat cards (value + label)
 *   cta          – centred call-to-action with title, text, button
 *   image_text   – image beside a text column
 *   features     – grid of feature cards (emoji/icon + title + description)
 */

import { useState, useEffect, useCallback } from "react";
import {
  Box, Container, Typography, Button, Grid, Paper, IconButton,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { Link } from "react-router-dom";

// ─── Hero ───────────────────────────────────────────────────────────────────────
function HeroBlock({ block }) {
  const {
    title = "",
    subtitle = "",
    bg_color = "#1a237e",
    text_color = "#ffffff",
    bg_image = "",
    cta_label = "",
    cta_url = "",
    min_height = 480,
    overlay_opacity = 0.45,
  } = block;

  const hasBgImage = Boolean(bg_image);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: min_height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        bgcolor: hasBgImage ? "transparent" : bg_color,
        backgroundImage: hasBgImage ? `url(${bg_image})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
      }}
    >
      {hasBgImage && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: bg_color,
            opacity: overlay_opacity,
          }}
        />
      )}
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, py: { xs: 8, md: 12 } }}>
        <Typography
          variant="h2"
          fontWeight={900}
          sx={{ color: text_color, fontSize: { xs: "2rem", md: "3.5rem" }, mb: 2, lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="h6"
            sx={{ color: text_color, opacity: 0.85, mb: 4, fontWeight: 400, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            {subtitle}
          </Typography>
        )}
        {cta_label && cta_url && (
          <Button
            component={cta_url.startsWith("http") ? "a" : Link}
            href={cta_url.startsWith("http") ? cta_url : undefined}
            to={cta_url.startsWith("http") ? undefined : cta_url}
            variant="contained"
            size="large"
            sx={{
              bgcolor: text_color,
              color: bg_color,
              fontWeight: 700,
              px: 5,
              py: 1.5,
              borderRadius: 8,
              "&:hover": { bgcolor: text_color, opacity: 0.9 },
            }}
          >
            {cta_label}
          </Button>
        )}
      </Container>
    </Box>
  );
}

// ─── Banner ─────────────────────────────────────────────────────────────────────
function BannerBlock({ block }) {
  const {
    text = "",
    bg_color = "#e91e63",
    text_color = "#ffffff",
    cta_label = "",
    cta_url = "",
  } = block;

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 1.5, md: 2 } }}>
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
          textAlign: "center",
        }}
      >
        <Typography variant="body1" fontWeight={600} sx={{ color: text_color }}>
          {text}
        </Typography>
        {cta_label && cta_url && (
          <Button
            component={cta_url.startsWith("http") ? "a" : Link}
            href={cta_url.startsWith("http") ? cta_url : undefined}
            to={cta_url.startsWith("http") ? undefined : cta_url}
            variant="outlined"
            size="small"
            sx={{
              color: text_color,
              borderColor: text_color,
              fontWeight: 700,
              "&:hover": { bgcolor: text_color, color: bg_color, borderColor: text_color },
            }}
          >
            {cta_label}
          </Button>
        )}
      </Container>
    </Box>
  );
}

// ─── Stats ───────────────────────────────────────────────────────────────────────
function StatsBlock({ block }) {
  const {
    items = [],
    bg_color = "#f5f7ff",
    text_color = "#1a237e",
    label_color = "#555",
    title = "",
  } = block;

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        {title && (
          <Typography
            variant="h5"
            fontWeight={700}
            textAlign="center"
            sx={{ color: text_color, mb: 4 }}
          >
            {title}
          </Typography>
        )}
        <Grid container spacing={3} justifyContent="center">
          {items.map((item, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Box textAlign="center">
                <Typography variant="h3" fontWeight={900} sx={{ color: text_color, lineHeight: 1 }}>
                  {item.value}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: label_color, mt: 0.5 }}>
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── CTA ─────────────────────────────────────────────────────────────────────────
function CtaBlock({ block }) {
  const {
    title = "",
    text = "",
    bg_color = "#1565c0",
    text_color = "#ffffff",
    cta_label = "",
    cta_url = "",
  } = block;

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 6, md: 10 }, textAlign: "center" }}>
      <Container maxWidth="sm">
        <Typography variant="h4" fontWeight={800} sx={{ color: text_color, mb: 2 }}>
          {title}
        </Typography>
        {text && (
          <Typography variant="body1" sx={{ color: text_color, opacity: 0.85, mb: 4 }}>
            {text}
          </Typography>
        )}
        {cta_label && cta_url && (
          <Button
            component={cta_url.startsWith("http") ? "a" : Link}
            href={cta_url.startsWith("http") ? cta_url : undefined}
            to={cta_url.startsWith("http") ? undefined : cta_url}
            variant="contained"
            size="large"
            sx={{
              bgcolor: text_color,
              color: bg_color,
              fontWeight: 700,
              px: 5,
              py: 1.5,
              borderRadius: 8,
              "&:hover": { bgcolor: text_color, opacity: 0.9 },
            }}
          >
            {cta_label}
          </Button>
        )}
      </Container>
    </Box>
  );
}

// ─── Image + Text ────────────────────────────────────────────────────────────────
function ImageTextBlock({ block }) {
  const {
    title = "",
    text = "",
    image = "",
    image_position = "left",
    bg_color = "#ffffff",
    text_color = "#1a1a1a",
    cta_label = "",
    cta_url = "",
  } = block;

  const imageFirst = image_position === "left";

  const imgCol = (
    <Grid item xs={12} md={6}>
      {image && (
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{ width: "100%", borderRadius: 3, objectFit: "cover", maxHeight: 380 }}
        />
      )}
    </Grid>
  );

  const textCol = (
    <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      {title && (
        <Typography variant="h4" fontWeight={800} sx={{ color: text_color, mb: 2 }}>
          {title}
        </Typography>
      )}
      {text && (
        <Typography variant="body1" sx={{ color: text_color, opacity: 0.82, lineHeight: 1.8, mb: 3 }}>
          {text}
        </Typography>
      )}
      {cta_label && cta_url && (
        <Box>
          <Button
            component={cta_url.startsWith("http") ? "a" : Link}
            href={cta_url.startsWith("http") ? cta_url : undefined}
            to={cta_url.startsWith("http") ? undefined : cta_url}
            variant="contained"
          >
            {cta_label}
          </Button>
        </Box>
      )}
    </Grid>
  );

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 5, md: 9 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={5} alignItems="center">
          {imageFirst ? (
            <>{imgCol}{textCol}</>
          ) : (
            <>{textCol}{imgCol}</>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── Features grid ───────────────────────────────────────────────────────────────
function FeaturesBlock({ block }) {
  const {
    title = "",
    items = [],
    bg_color = "#ffffff",
    text_color = "#1a237e",
    columns = 3,
  } = block;

  const mdCols = Math.min(Math.max(Math.round(12 / columns), 2), 6);

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 5, md: 9 } }}>
      <Container maxWidth="lg">
        {title && (
          <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ color: text_color, mb: 5 }}>
            {title}
          </Typography>
        )}
        <Grid container spacing={3}>
          {items.map((item, i) => (
            <Grid item xs={12} sm={6} md={mdCols} key={i}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: "100%",
                  textAlign: "center",
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                {item.emoji && (
                  <Typography variant="h3" sx={{ mb: 1.5 }}>
                    {item.emoji}
                  </Typography>
                )}
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: text_color, mb: 1 }}>
                  {item.title}
                </Typography>
                {item.description && (
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── Spacer ──────────────────────────────────────────────────────────────────────
function SpacerBlock({ block }) {
  const { height = 60, bg_color = "transparent" } = block;
  return <Box sx={{ height, bgcolor: bg_color }} />;
}

// ─── YouTube video ───────────────────────────────────────────────────────────────
function extractYouTubeId(url) {
  if (!url) return "";
  const shortMatch = url.match(/youtu\.be\/([^?&#/]+)/);
  if (shortMatch) return shortMatch[1];
  const longMatch = url.match(/[?&]v=([^&#]+)/);
  if (longMatch) return longMatch[1];
  const embedMatch = url.match(/\/embed\/([^?&#]+)/);
  if (embedMatch) return embedMatch[1];
  if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) return url.trim();
  return "";
}

function YoutubeBlock({ block }) {
  const { url = "", title = "", aspect_ratio = "16/9", bg_color = "#000000" } = block;
  const videoId = extractYouTubeId(url);
  const [num, den] = aspect_ratio.split("/").map(Number);
  const paddingTop = den && num ? `${(den / num) * 100}%` : "56.25%";

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 3, md: 5 } }}>
      <Container maxWidth="md">
        {videoId ? (
          <Box sx={{ position: "relative", paddingTop, borderRadius: 2, overflow: "hidden" }}>
            <Box
              component="iframe"
              src={`https://www.youtube-nocookie.com/embed/${videoId}`}
              title={title || "YouTube video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </Box>
        ) : (
          <Box sx={{ bgcolor: "#111", height: 240, borderRadius: 2 }} />
        )}
        {title && (
          <Typography variant="body2" textAlign="center" sx={{ color: "#ccc", mt: 1.5 }}>
            {title}
          </Typography>
        )}
      </Container>
    </Box>
  );
}

// ─── Local video ─────────────────────────────────────────────────────────────────
function VideoBlock({ block }) {
  const { url = "", title = "", poster = "", bg_color = "#000000" } = block;

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 3, md: 5 } }}>
      <Container maxWidth="md">
        {url ? (
          <Box
            component="video"
            src={url}
            poster={poster || undefined}
            controls
            sx={{ width: "100%", borderRadius: 2, display: "block" }}
          />
        ) : (
          <Box sx={{ bgcolor: "#111", height: 240, borderRadius: 2 }} />
        )}
        {title && (
          <Typography variant="body2" textAlign="center" sx={{ color: "#ccc", mt: 1.5 }}>
            {title}
          </Typography>
        )}
      </Container>
    </Box>
  );
}

// ─── Google Map ──────────────────────────────────────────────────────────────────
function MapBlock({ block }) {
  const { embed_url = "", title = "", height = 400 } = block;

  return (
    <Box sx={{ py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        {title && (
          <Typography variant="h5" fontWeight={700} textAlign="center" sx={{ mb: 3 }}>
            {title}
          </Typography>
        )}
        {embed_url ? (
          <Box
            component="iframe"
            src={embed_url}
            title={title || "Google Map"}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            sx={{
              width: "100%",
              height,
              border: "none",
              borderRadius: 2,
              display: "block",
            }}
          />
        ) : (
          <Box sx={{ bgcolor: "#e0e0e0", height, borderRadius: 2 }} />
        )}
      </Container>
    </Box>
  );
}

// ─── Google Form ─────────────────────────────────────────────────────────────────
function GoogleFormBlock({ block }) {
  const { embed_url = "", title = "", height = 600 } = block;

  return (
    <Box sx={{ py: { xs: 3, md: 5 } }}>
      <Container maxWidth="md">
        {title && (
          <Typography variant="h5" fontWeight={700} textAlign="center" sx={{ mb: 3 }}>
            {title}
          </Typography>
        )}
        {embed_url ? (
          <Box
            component="iframe"
            src={embed_url}
            title={title || "Google Form"}
            sx={{
              width: "100%",
              height,
              border: "none",
              borderRadius: 2,
              display: "block",
            }}
          />
        ) : (
          <Box sx={{ bgcolor: "#e0e0e0", height, borderRadius: 2 }} />
        )}
      </Container>
    </Box>
  );
}

// ─── Image Gallery ───────────────────────────────────────────────────────────────
function GalleryBlock({ block }) {
  const { title = "", items = [], columns = 3, bg_color = "#ffffff" } = block;
  const mdCols = Math.min(Math.max(Math.round(12 / columns), 3), 6);

  return (
    <Box sx={{ bgcolor: bg_color, py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        {title && (
          <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ mb: 5 }}>
            {title}
          </Typography>
        )}
        <Grid container spacing={2}>
          {items.map((item, i) => (
            <Grid item xs={12} sm={6} md={mdCols} key={i}>
              <Box sx={{ borderRadius: 2, overflow: "hidden", position: "relative" }}>
                {item.image ? (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.caption || `Photo ${i + 1}`}
                    sx={{
                      width: "100%",
                      aspectRatio: "4/3",
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 0.3s",
                      "&:hover": { transform: "scale(1.04)" },
                    }}
                  />
                ) : (
                  <Box sx={{ bgcolor: "#e0e0e0", aspectRatio: "4/3" }} />
                )}
                {item.caption && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      py: 0.75,
                      color: "text.secondary",
                    }}
                  >
                    {item.caption}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── Image Slider ────────────────────────────────────────────────────────────────
function SliderBlock({ block }) {
  const {
    title = "",
    items = [],
    autoplay = true,
    interval = 4000,
    show_captions = true,
  } = block;

  const [active, setActive] = useState(0);
  const count = items.length;

  const next = useCallback(() => setActive((a) => (a + 1) % count), [count]);
  const prev = () => setActive((a) => (a - 1 + count) % count);

  useEffect(() => {
    if (!autoplay || count < 2) return;
    const id = setInterval(next, Number(interval) || 4000);
    return () => clearInterval(id);
  }, [autoplay, interval, count, next]);

  if (count === 0) return null;

  const current = items[active];

  return (
    <Box sx={{ py: { xs: 3, md: 5 } }}>
      {title && (
        <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ mb: 3 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ position: "relative", maxWidth: 960, mx: "auto", userSelect: "none" }}>
        {current.image ? (
          <Box
            component="img"
            src={current.image}
            alt={current.caption || `Slide ${active + 1}`}
            sx={{
              width: "100%",
              maxHeight: 520,
              objectFit: "cover",
              display: "block",
              borderRadius: 3,
            }}
          />
        ) : (
          <Box sx={{ bgcolor: "#e0e0e0", height: 360, borderRadius: 3 }} />
        )}
        {/* Caption */}
        {show_captions && current.caption && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "rgba(0,0,0,0.52)",
              color: "#fff",
              px: 3,
              py: 1.5,
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
            }}
          >
            <Typography variant="body1" fontWeight={500}>
              {current.caption}
            </Typography>
          </Box>
        )}
        {/* Prev / Next arrows */}
        {count > 1 && (
          <>
            <IconButton
              onClick={prev}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.45)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ArrowBackIos fontSize="small" />
            </IconButton>
            <IconButton
              onClick={next}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.45)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </>
        )}
        {/* Dot indicators */}
        {count > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1.5 }}>
            {items.map((_, i) => (
              <Box
                key={i}
                onClick={() => setActive(i)}
                sx={{
                  width: i === active ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: i === active ? "primary.main" : "grey.400",
                  cursor: "pointer",
                  transition: "all 0.25s",
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Root renderer ───────────────────────────────────────────────────────────────
export default function BlockRenderer({ blocks = [] }) {
  if (!blocks || blocks.length === 0) return null;

  return (
    <>
      {blocks.map((block, i) => {
        switch (block.type) {
          case "hero":
            return <HeroBlock key={block.id || i} block={block} />;
          case "banner":
            return <BannerBlock key={block.id || i} block={block} />;
          case "stats":
            return <StatsBlock key={block.id || i} block={block} />;
          case "cta":
            return <CtaBlock key={block.id || i} block={block} />;
          case "image_text":
            return <ImageTextBlock key={block.id || i} block={block} />;
          case "features":
            return <FeaturesBlock key={block.id || i} block={block} />;
          case "spacer":
            return <SpacerBlock key={block.id || i} block={block} />;
          case "richtext":
            return (
              <Box key={block.id || i} sx={{ py: { xs: 3, md: 5 } }}>
                <Container maxWidth="md">
                  <Box className="prose-content" dangerouslySetInnerHTML={{ __html: block.html || "" }} />
                </Container>
              </Box>
            );
          case "youtube":
            return <YoutubeBlock key={block.id || i} block={block} />;
          case "video":
            return <VideoBlock key={block.id || i} block={block} />;
          case "map":
            return <MapBlock key={block.id || i} block={block} />;
          case "google_form":
            return <GoogleFormBlock key={block.id || i} block={block} />;
          case "gallery":
            return <GalleryBlock key={block.id || i} block={block} />;
          case "slider":
            return <SliderBlock key={block.id || i} block={block} />;
          default:
            return null;
        }
      })}
    </>
  );
}
